/**
 * Timezone-based geographic region detection
 * Maps IANA timezone identifiers to approximate geographic bounds
 * for initial map positioning without requiring user permissions
 */

// Region bounds definitions for North American timezones
const TIMEZONE_REGIONS = {
  // Pacific Time Zone (US & Canada)
  'America/Los_Angeles': {
    ne: { lat: 49.0, lng: -117.0 },
    sw: { lat: 32.5, lng: -125.0 },
  },
  'America/Vancouver': {
    ne: { lat: 54.0, lng: -114.0 },
    sw: { lat: 48.0, lng: -126.0 },
  },
  'America/Tijuana': {
    ne: { lat: 32.5, lng: -116.0 },
    sw: { lat: 28.0, lng: -118.0 },
  },

  // Mountain Time Zone (US & Canada)
  'America/Denver': {
    ne: { lat: 49.0, lng: -102.0 },
    sw: { lat: 31.0, lng: -114.0 },
  },
  'America/Edmonton': {
    ne: { lat: 60.0, lng: -110.0 },
    sw: { lat: 49.0, lng: -120.0 },
  },
  'America/Phoenix': {
    ne: { lat: 37.0, lng: -109.0 },
    sw: { lat: 31.3, lng: -114.8 },
  },
  'America/Boise': {
    ne: { lat: 49.0, lng: -111.0 },
    sw: { lat: 42.0, lng: -117.0 },
  },

  // Central Time Zone (US, Canada & Mexico)
  'America/Chicago': {
    ne: { lat: 49.0, lng: -87.0 },
    sw: { lat: 25.8, lng: -104.0 },
  },
  'America/Winnipeg': {
    ne: { lat: 60.0, lng: -95.0 },
    sw: { lat: 49.0, lng: -102.0 },
  },
  'America/Mexico_City': {
    ne: { lat: 32.7, lng: -86.7 },
    sw: { lat: 14.5, lng: -118.4 },
  },
  'America/Regina': {
    ne: { lat: 60.0, lng: -101.0 },
    sw: { lat: 49.0, lng: -110.0 },
  },

  // Eastern Time Zone (US & Canada)
  'America/New_York': {
    ne: { lat: 47.5, lng: -66.9 },
    sw: { lat: 24.5, lng: -87.0 },
  },
  'America/Toronto': {
    ne: { lat: 56.0, lng: -74.0 },
    sw: { lat: 41.7, lng: -95.0 },
  },
  'America/Detroit': {
    ne: { lat: 48.3, lng: -82.4 },
    sw: { lat: 41.7, lng: -90.4 },
  },
  'America/Montreal': {
    ne: { lat: 62.0, lng: -57.0 },
    sw: { lat: 45.0, lng: -79.0 },
  },
  'America/Halifax': {
    ne: { lat: 47.0, lng: -59.0 },
    sw: { lat: 43.4, lng: -66.4 },
  },

  // Alaska
  'America/Anchorage': {
    ne: { lat: 71.5, lng: -130.0 },
    sw: { lat: 51.0, lng: -169.0 },
  },
  'America/Juneau': {
    ne: { lat: 71.5, lng: -130.0 },
    sw: { lat: 54.0, lng: -141.0 },
  },

  // Hawaii
  'Pacific/Honolulu': {
    ne: { lat: 22.5, lng: -154.8 },
    sw: { lat: 18.9, lng: -160.5 },
  },

  // Atlantic Time
  'America/Puerto_Rico': {
    ne: { lat: 18.5, lng: -65.2 },
    sw: { lat: 17.9, lng: -67.3 },
  },
  'America/St_Johns': {
    ne: { lat: 51.7, lng: -52.6 },
    sw: { lat: 46.6, lng: -59.5 },
  },
};

// Fallback to full North America view
const NORTH_AMERICA_BOUNDS = {
  ne: { lat: 72.0, lng: -50.0 },
  sw: { lat: 15.0, lng: -170.0 },
};

/**
 * Get the user's timezone using the Intl API
 * @returns {string|null} IANA timezone identifier (e.g., "America/New_York") or null if unavailable
 */
export const getUserTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone || null;
  } catch (e) {
    // Browser doesn't support timezone detection
    return null;
  }
};

/**
 * Get geographic bounds based on user's detected timezone
 * @returns {Object} LatLngBounds object with ne/sw properties
 */
export const getTimezoneBasedBounds = () => {
  const timezone = getUserTimezone();

  if (!timezone) {
    return NORTH_AMERICA_BOUNDS;
  }

  // Direct match
  if (TIMEZONE_REGIONS[timezone]) {
    return TIMEZONE_REGIONS[timezone];
  }

  // Fallback patterns for similar timezones
  // e.g., "America/Indiana/Indianapolis" -> use America/New_York bounds
  if (timezone.startsWith('America/Indiana/') || timezone.includes('Kentucky')) {
    return TIMEZONE_REGIONS['America/New_York'];
  }

  if (timezone.startsWith('America/North_Dakota/')) {
    return TIMEZONE_REGIONS['America/Chicago'];
  }

  if (timezone.includes('Pacific/')) {
    return TIMEZONE_REGIONS['Pacific/Honolulu'];
  }

  // General region fallbacks based on timezone prefix
  if (timezone.startsWith('America/')) {
    const parts = timezone.split('/');
    const city = parts[parts.length - 1];

    // Try to infer region from common city patterns
    const easternCities = ['New_York', 'Toronto', 'Detroit', 'Miami', 'Boston', 'Atlanta'];
    const centralCities = ['Chicago', 'Dallas', 'Houston', 'Winnipeg', 'Mexico_City'];
    const mountainCities = ['Denver', 'Phoenix', 'Salt_Lake_City', 'Calgary'];
    const pacificCities = ['Los_Angeles', 'Seattle', 'San_Francisco', 'Vancouver', 'Portland'];

    if (easternCities.some(c => city.includes(c))) {
      return TIMEZONE_REGIONS['America/New_York'];
    }
    if (centralCities.some(c => city.includes(c))) {
      return TIMEZONE_REGIONS['America/Chicago'];
    }
    if (mountainCities.some(c => city.includes(c))) {
      return TIMEZONE_REGIONS['America/Denver'];
    }
    if (pacificCities.some(c => city.includes(c))) {
      return TIMEZONE_REGIONS['America/Los_Angeles'];
    }
  }

  // Default to North America if no match found
  return NORTH_AMERICA_BOUNDS;
};

/**
 * Get a human-readable region name based on timezone
 * @returns {string} Region name or "North America" as fallback
 */
export const getTimezoneRegionName = () => {
  const timezone = getUserTimezone();

  if (!timezone) {
    return 'North America';
  }

  const regionMap = {
    'America/Los_Angeles': 'West Coast',
    'America/Vancouver': 'Pacific Northwest',
    'America/Denver': 'Rocky Mountains',
    'America/Phoenix': 'Southwest',
    'America/Chicago': 'Central US',
    'America/Mexico_City': 'Mexico',
    'America/New_York': 'Northeast',
    'America/Toronto': 'Eastern Canada',
    'America/Anchorage': 'Alaska',
    'Pacific/Honolulu': 'Hawaii',
  };

  return regionMap[timezone] || 'North America';
};
